import {colors} from './colors';
import useWindowDimensions from '../utils/hooks/useWindowDimensions';
import {getByScreenSize, hdp, wdp} from '../utils/responsive';
import {Theme} from './lightTheme';

/**
 * Defines the dark theme for the application.
 *
 * This function returns a comprehensive object containing styles and colors for the dark theme.
 *
 * @returns - An object representing the dark theme, including color palettes, fonts, text styles, and component-specific styles.
 */
const height = window.innerHeight;

const darkTheme = (): Theme => {
  const {isMobile} = useWindowDimensions();
  return {
    primary: '#006562',
    secondary: '#004a6c',
    homeBackground: '#181818',
    primaryOverlay: 'rgba(0,40,43,0.7)',
    border: '#D9D9D9',
    shadow: 'rgba(0,0,0,0.4)',
    ripple: colors.ripple,
    skeletonBackground: '#aaaaaa',
    overlay: 'rgba(0,0,0,0.3)',
    overlayScreen: 'rgba(0,0,0,0.6)',
    defaultOverlayScreen: 'rgba(0,0,0,0.2)',
    dimensions: {
      hdp: (percent: number) => hdp(percent),
      wdp: (percent: number) => wdp(percent),
      getByScreenSize: (smValue: any, lgValue: any, xsmValue: any = smValue) =>
        getByScreenSize(smValue, lgValue, xsmValue),
    },
    isMobile,
    alert: {
      backgroundPrimary: colors.primary,
      textPrimary: '#FFFFFF',
    },
    fonts: {
      ar: '"Ubuntu", Arial, sans-serif',
      en: '"Helvetica", Arial, sans-serif',
    },
    text: {
      dark: colors.title,
      black: '#181818',
      border: '#CCCCCC',
      icon: colors.icon,
      error: colors.error,
      grey: colors.description,
      light: '#ffffff',
      title: '#f5f5f5',
      description: '#cccccc',
      textPrimary: '#242424',
      placeholder: '#FFFFFF',
      borderLight: '#FFFFFF',
      borderFocus: '#80CBC4',
      s1: height >= 900 ? '4.5rem' : '4rem',
      s2: height >= 900 ? '4rem' : '3.25rem',
      s3: height >= 900 ? '2.75rem' : '2rem',
      s35: height >= 900 ? '1.8rem' : '1.4rem',
      s4: height >= 900 ? '1.5rem' : '1.2rem',
      s5: height >= 900 ? '1.4rem' : '1rem',
      s55: height >= 900 ? '1.2rem' : '0.8rem',
      s6: height >= 900 ? '1.2rem' : '0.8rem',
      s7: height >= 900 ? '1rem' : '0.75rem',
      s8: height >= 900 ? '0.875rem' : '0.7rem',
      s85: height >= 900 ? '0.875rem' : '0.7rem',
      s89: height >= 900 ? '0.825rem' : '0.675rem',
      s9: height >= 900 ? '0.8rem' : '0.65rem',
      s10: height >= 900 ? '0.7rem' : '0.6rem',
      s11: height >= 900 ? '0.65rem' : '0.55rem',
      s12: height >= 900 ? '0.5rem' : '0.4rem',
    },
    button: {
      text: '#FFFFFF',
      lightColor: '#FFFFFF',
      outline: colors.primary,
    },
    textInput: {
      cursorColor: colors.primary,
      placeholder: '#DDDDDD',
      border: '#ffffff',
      required: '#ffd2d2',
    },
    drawer: {
      bgColor1: '#006562AA',
      bgColor2: '#004a6cAA',
      fontColor: '#ffffff',
      fontColorSelected: '#D9D9D9',
      dividerColor: '#CCCCCC',
      selectedBackgroundCollapsedMode: 'dark',
    },
    voiceQuestionInput: {
      text: '#f5f5f5',
      background: '#181818',
      inputPlaceholder: 'rgba(255,255,255,0.2)',
      iosEndColor: 'rgba(0,0,0,0)',
      mainButtonStartColor: '#6c6c6c',
      mainButtonEndColor: '#181818',
    },
    afterLogin: {
      contentBackground: '#181818',
      text: '#f5f5f5',
      label: '#5d5d5d',
      line: '#999',
      selected: 'rgba(255,255,255,0.1)',
      checkIcon: '#3b3b3b',
      checkIconBackground: colors.primary,
    },
  };
};

export default darkTheme;
